import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ShieldCheck } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const API_BASE = API_URL.replace('/api', '');

export default function Login() {
  const { login, completeLogin } = useAuth();
  const { tenant, isLegacyDnfHost } = useTenant();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [notVerified, setNotVerified] = useState(false);
  // Étape MFA
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNotVerified(false);
    try {
      const data = await login(email, password);
      if (data.mfaRequired) {
        setMfaToken(data.mfaToken);
      } else if (data.mfaSetupRequired) {
        navigate('/mfa-enrolement', { state: { mfaToken: data.mfaToken } });
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      if (err.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
        setNotVerified(true);
      } else {
        toast.error(err.response?.data?.error || 'Erreur de connexion');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/mfa/verify', { mfaToken, code: code.trim() });
      completeLogin(data);
      navigate('/dashboard');
    } catch (err) {
      if (err.response?.status === 401 && err.response?.data?.error?.includes('expirée')) {
        toast.error(err.response.data.error);
        setMfaToken(null);
        setCode('');
      } else {
        toast.error(err.response?.data?.error || 'Code incorrect');
      }
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      const { data } = await api.post('/auth/resend-verification', { email });
      toast.success(data.message);
    } catch {
      toast.error('Erreur, réessayez plus tard');
    }
  };

  if (mfaToken) {
    return (
      <div className="auth-page">
        <div className="auth-card card">
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <ShieldCheck size={40} color="#2563eb" style={{ margin: '0 auto 10px' }} />
            <h2 style={{ margin: 0 }}>Vérification en deux étapes</h2>
            <p className="auth-subtitle">
              Saisissez le code de votre application d'authentification,<br />
              ou un code de récupération.
            </p>
          </div>
          <form onSubmit={handleMfaSubmit}>
            <div className="form-group">
              <label>Code</label>
              <input value={code} onChange={e => setCode(e.target.value)} required autoFocus
                autoComplete="one-time-code" placeholder="123456 ou XXXXX-XXXXX"
                style={{ textAlign: 'center', letterSpacing: 2, fontSize: '1.1rem' }} />
            </div>
            <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} disabled={loading}>
              {loading ? 'Vérification...' : 'Valider'}
            </button>
          </form>
          <p style={{ textAlign: 'center', marginTop: 16, fontSize: '0.9rem' }}>
            <button type="button" onClick={() => { setMfaToken(null); setCode(''); }}
              style={{ background: 'none', border: 'none', color: 'var(--text-light)', cursor: 'pointer', textDecoration: 'underline' }}>
              Retour à la connexion
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card card">
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          {tenant ? (
            <>
              {tenant.logo ? (
                <img src={`${API_BASE}/uploads/${tenant.logo}`} alt={tenant.name}
                  style={{ height: 64, width: 'auto', margin: '0 auto 12px', display: 'block' }} />
              ) : isLegacyDnfHost ? (
                <img src="/logo.png" alt={tenant.name} style={{ height: 64, width: 'auto', margin: '0 auto 12px', display: 'block', mixBlendMode: 'multiply' }} />
              ) : null}
              <h1 style={{ margin: 0 }}>{tenant.name}</h1>
              <p className="auth-subtitle">Suivi de chantier</p>
            </>
          ) : isLegacyDnfHost ? (
            <>
              <img src="/logo.png" alt="DNF" style={{ height: 64, width: 'auto', margin: '0 auto 12px', display: 'block', mixBlendMode: 'multiply' }} />
              <h1 style={{ margin: 0 }}>DNF sas</h1>
              <p className="auth-subtitle">Suivi de chantier</p>
            </>
          ) : (
            <>
              <h1 style={{ margin: 0 }}>Jalonne</h1>
              <p className="auth-subtitle">Le suivi de chantier par QR code</p>
            </>
          )}
        </div>

        {notVerified && (
          <div style={{
            background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8,
            padding: '12px 14px', marginBottom: 16, fontSize: '0.88rem', color: '#92400e', lineHeight: 1.5,
          }}>
            Votre adresse email n'est pas encore confirmée. Vérifiez votre boîte de réception.
            <button type="button" onClick={resend} style={{
              display: 'block', marginTop: 8, background: 'none', border: 'none',
              color: '#92400e', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', padding: 0,
            }}>
              Renvoyer l'email de confirmation
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
          </div>
          <div className="form-group">
            <label>Mot de passe</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required />
          </div>
          <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} disabled={loading}>
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: 14, fontSize: '0.88rem' }}>
          <Link to="/mot-de-passe-oublie" style={{ color: 'var(--text-light)' }}>Mot de passe oublié ?</Link>
        </p>
      </div>
    </div>
  );
}
